import { useEffect, useRef, useState } from "react";

import { StockfishWorkerClient } from "@/pages/analysis/engine/stockfishWorkerClient";
import type {
  StockfishEvaluation,
  StockfishStatus,
} from "@/pages/analysis/engine/stockfishTypes";

type UseStockfishEvaluationOptions = {
  fen: string | null;
  active?: boolean;
  depth?: number;
  multiPv?: number;
  enabledByDefault?: boolean;
};

export function useStockfishEvaluation({
  fen,
  active = true,
  depth = 14,
  multiPv = 5,
  enabledByDefault = true,
}: UseStockfishEvaluationOptions) {
  const [enabled, setEnabled] = useState(enabledByDefault);
  const [status, setStatus] = useState<StockfishStatus>("idle");
  const [evaluation, setEvaluation] = useState<StockfishEvaluation | null>(null);
  const clientRef = useRef<StockfishWorkerClient | null>(null);

  useEffect(() => {
    if (!active || !enabled) {
      clientRef.current?.destroy();
      clientRef.current = null;
      setStatus("idle");
      setEvaluation(null);
      return;
    }

    const client = new StockfishWorkerClient({ depth, multiPv });
    clientRef.current = client;
    const unsubscribe = client.subscribe((event) => {
      if (event.type === "status") {
        setStatus(event.status);
        return;
      }

      setEvaluation(event.evaluation);
      setStatus(event.evaluation.status);
    });

    client.start();

    return () => {
      unsubscribe();
      client.destroy();
      clientRef.current = null;
    };
  }, [active, depth, enabled, multiPv]);

  useEffect(() => {
    if (active && enabled && fen) {
      clientRef.current?.analyze(fen);
    }
  }, [active, enabled, fen]);

  const currentEvaluation = evaluation?.fen === fen ? evaluation : null;

  return {
    enabled,
    setEnabled,
    status,
    evaluation: currentEvaluation,
  };
}
